import type { EntryValueViewT } from "@patient-watch/types";

function isBareCode(text: string, numberValue: number | null | undefined): boolean {
    if (numberValue != null && text === String(numberValue)) return true;
    return /^-?[0-9]+(\.[0-9]+)?$/.test(text);
}

/** Dictionary / single-choice label. Prefer the linked option, never a bare code when a label exists. */
export function choiceEntryValueLabel(
    ev: Pick<EntryValueViewT, "text_value" | "number_value" | "value" | "options">
): string {
    const options = ev.options ?? [];
    const linkedLabel = options.find((option) => option.label?.trim())?.label?.trim();
    if (linkedLabel) return linkedLabel;

    const text = ev.text_value?.trim();
    if (text && !isBareCode(text, ev.number_value)) return text;

    const code = text ?? (ev.number_value != null ? String(ev.number_value) : null);
    const matched = options.find((option) => String(option.value ?? "") === code);
    if (matched?.label?.trim()) return matched.label.trim();

    if (text) return text;
    if (ev.number_value != null) return String(ev.number_value);
    if (ev.value != null && ev.value !== "") return String(ev.value);
    return "N/A";
}

const OPTION_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An own option ("add your own option") is stored as its option id. */
export function isOptionId(text: string): boolean {
    return OPTION_ID.test(text.trim());
}

/** Stored answers in order: the array, a Postgres array string such as {11,abc}, or the single value. */
function storedChoices(ev: Pick<EntryValueViewT, "text_value" | "number_value" | "value" | "text_array">): string[] {
    if (ev.text_array?.length) return ev.text_array.map((item) => item.trim()).filter(Boolean);
    const value: unknown = ev.value;
    if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
    if (typeof value === "string" && value.startsWith("{") && value.endsWith("}")) {
        return value
            .slice(1, -1)
            .split(",")
            .map((item) => item.trim().replace(/^"|"$/g, ""))
            .filter(Boolean);
    }
    const single = ev.text_value?.trim() || (ev.number_value != null ? String(ev.number_value) : "");
    return single ? [single] : [];
}

/**
 * Multiple-choice labels in answer order. Each stored code or own-option id becomes its linked option's label.
 * Anything with no linked option is left as stored, so the caller can look up own options by id.
 */
export function choiceEntryValueLabels(
    ev: Pick<EntryValueViewT, "text_value" | "number_value" | "value" | "text_array" | "options">
): string[] {
    const options = ev.options ?? [];
    return storedChoices(ev).map((stored) => {
        const matched = options.find((option) => String(option.value ?? "") === stored);
        return matched?.label?.trim() || stored;
    });
}
